import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { FetchClientSearchRequest } from '../model/customer.model'
import { fetchClientSearch } from '../store/customer.reducer'
import {
  selectClientSearch,
  selectClientSearchInvalidContract,
  selectClientSearchWithoutClient,
} from '../store/customer.selectors'

import {
  CLIENT_SEARCH_INVALID_CONTRACT,
  CLIENT_SEARCH_WITHOUT_CLIENT,
  CLIENT_SEARCH_SERVICE_ERROR,
  CLIENT_SEARCH_SERVICE_ERRONEOUS_PARAMS,
  CLIENT_SEARCH_SERVICE_LOADING,
} from '~/modal/constants/messages'
import useModal from '~/modal/hooks/useModal'
import ModalMessages from '~/modal/models/modal.model'
import { changeSaleFlow } from '~/sale/store'

export default function useClientSearch({
  brand,
  body: { validationType, value, contractId },
}: {
  brand: string
  body: FetchClientSearchRequest
}) {
  const dispatch = useDispatch()
  const { httpStatus } = useSelector(selectClientSearch)
  const withoutClientErrorType = useSelector(selectClientSearchWithoutClient)
  const invalidContractErrorType = useSelector(selectClientSearchInvalidContract)
  const { selectAndOpenModalFromList, setModalConfig } = useModal()

  function dispatchClientSearch(tenant: string, body: FetchClientSearchRequest) {
    selectAndOpenModalFromList('CLIENT_SEARCH_SERVICE_LOADING', clientSearchModalMessages)
    return dispatch(fetchClientSearch({ tenant, body }))
  }

  const clientSearchModalMessages: ModalMessages = {
    CLIENT_SEARCH_INVALID_CONTRACT: {
      open: false,
      title: CLIENT_SEARCH_INVALID_CONTRACT.title,
      description: CLIENT_SEARCH_INVALID_CONTRACT.description,
      actions: [
        {
          button: { variant: 'contained', size: 'medium', color: 'secondary' },
          name: CLIENT_SEARCH_INVALID_CONTRACT.actions.continue,
          dataHook: 'continue-client-search',
        },
      ],
      type: 'warning',
    },
    CLIENT_SEARCH_WITHOUT_CLIENT: {
      open: false,
      title: CLIENT_SEARCH_WITHOUT_CLIENT.title,
      description: CLIENT_SEARCH_WITHOUT_CLIENT.description,
      actions: [
        {
          button: { variant: 'contained', size: 'medium', color: 'secondary' },
          name: CLIENT_SEARCH_WITHOUT_CLIENT.actions.continue,
          dataHook: 'continue-client-search',
        },
      ],
      type: 'error',
    },
    CLIENT_SEARCH_SERVICE_ERROR: {
      open: false,
      title: CLIENT_SEARCH_SERVICE_ERROR.title,
      description: CLIENT_SEARCH_SERVICE_ERROR.description,
      actions: [
        {
          button: { variant: 'contained', size: 'medium', color: 'secondary' },
          name: CLIENT_SEARCH_SERVICE_ERROR.actions.retry,
          fn: () => dispatchClientSearch(brand, { validationType, value, contractId }),
          dataHook: 'retry-client-search',
        },
      ],
      type: 'error',
    },
    CLIENT_SEARCH_SERVICE_ERRONEOUS_PARAMS: {
      open: false,
      title: CLIENT_SEARCH_SERVICE_ERRONEOUS_PARAMS.title,
      description: CLIENT_SEARCH_SERVICE_ERRONEOUS_PARAMS.description,
      actions: [
        {
          button: { variant: 'contained', size: 'medium', color: 'secondary' },
          name: CLIENT_SEARCH_SERVICE_ERRONEOUS_PARAMS.actions.continue,
          dataHook: 'continue-client-search',
        },
      ],
      type: 'error',
    },
    CLIENT_SEARCH_SERVICE_LOADING: {
      open: false,
      title: CLIENT_SEARCH_SERVICE_LOADING.title,
      description: CLIENT_SEARCH_SERVICE_LOADING.description,
      actions: [],
      type: 'loading',
    },
  }

  useEffect(() => {
    if ((brand && validationType && value && contractId) || (brand && validationType && value)) {
      dispatchClientSearch(brand, {
        validationType,
        value,
        contractId,
      })
    }
  }, [brand, validationType, value, contractId])

  useEffect(() => {
    if (httpStatus && httpStatus === 500) {
      selectAndOpenModalFromList('CLIENT_SEARCH_SERVICE_ERROR', clientSearchModalMessages)
    }
    if (httpStatus && httpStatus === 404 && withoutClientErrorType)
      selectAndOpenModalFromList(withoutClientErrorType, clientSearchModalMessages)
    if (httpStatus && httpStatus === 400) {
      selectAndOpenModalFromList(
        'CLIENT_SEARCH_SERVICE_ERRONEOUS_PARAMS',
        clientSearchModalMessages,
      )
    }
    if (httpStatus && httpStatus === 200 && invalidContractErrorType) {
      dispatch(changeSaleFlow({ isCartera: true }))
      selectAndOpenModalFromList(invalidContractErrorType, clientSearchModalMessages)
    }
    if (httpStatus && httpStatus === 200 && !invalidContractErrorType) {
      dispatch(changeSaleFlow({ isCartera: true }))
      setModalConfig({ open: false, type: '', actions: [] })
    }
  }, [httpStatus, withoutClientErrorType, invalidContractErrorType])
}
